<script lang="ts" setup>
import { Ref, ref, watch } from 'vue';
import { IGroupedRow, IGroupedRowActionConfig, IGroupedRowColumnConfig, IGroupedRowItem, IGroupedRowSortDirection, IGroupedRowSortState } from '../../lib/Models/GroupedRowsTable';
import Icon from '../../components/Icon.vue';
import { IconTypes, ButtonDisplaySize } from '../../Enums/UiEnums';
import ChoiceButton from '../Buttons/ChoiceButton.vue';

const props = withDefaults(defineProps<{
	title: string,
	columns: IGroupedRowColumnConfig[],
	groupedRows: IGroupedRow[],
	actionConfig?: IGroupedRowActionConfig,
}>(), {});

const rows: Ref<IGroupedRowItem[]> = ref([]);
const sortStates: Ref<IGroupedRowSortState[]> = ref([]);

watch(
	() => props.groupedRows,
	() => {
		rows.value = props.groupedRows.flatMap(group => group.rows);
		sortStates.value = props.columns.map(column => { return { key: column.key, state: undefined } });
	}, 
	{ immediate: true }
);

function updateSortState(columnKey: string): IGroupedRowSortState | undefined {
	let sortState = sortStates.value.find(sortState => sortState.key === columnKey);
	if (sortState) {
		sortState.state = sortState.state === undefined ?
							IGroupedRowSortDirection.ASC : sortState.state === IGroupedRowSortDirection.ASC ?
							IGroupedRowSortDirection.DESC : undefined;
	}
	// reset all other sorts
	sortStates.value.forEach(sortState => { if (sortState.key !== columnKey) { sortState.state = undefined; }});
	return sortState;
}

function sortColumn(column: IGroupedRowColumnConfig): void {
	let sortState = updateSortState(column.key);
	if (sortState?.state !== undefined) {
		column.sortConfig?.sort(rows.value, sortState?.state);
	}
}

function getSortIcon(columnKey: string): IconTypes {
	let sortState = sortStates.value.find(sortState => sortState.key === columnKey);
	return sortState?.state === IGroupedRowSortDirection.ASC ?
			IconTypes.SortUp : sortState?.state === IGroupedRowSortDirection.DESC ?
			IconTypes.SortDown : IconTypes.Sort;
}

function sortIsSet(): boolean {
	return sortStates.value.some(state => state.state !== undefined);
}

</script>

<style scoped>
    #groupedRowsTable * {
        font-size: x-small;
    }
	#title {
        font-size: small;
    }
	.pointer {
		cursor: pointer;
	}
	.header {
		background-color: var(--bs-border-color);
	}
</style>

<template>
	<div id="groupedRowsTable" class="w-100 h-100">
		<div id="title" class="header text-dark border rounded-top fw-bold mt-1 py-1 ps-2">{{ props.title }}</div>
		<div class="d-flex w-100 ps-2 py-1 bg-light border border-bottom" style="gap: 1%;">
			<template v-for="(column) in props.columns">
				<div
					:style="`width: ${column.widthPercent}%`" 
					:class="{ 'pointer': column.sortConfig }"
					@click="sortColumn(column)"
				>
					<div class="fw-bold d-inline">{{ column.name }}</div>
					<div v-if="column.sortConfig" class="d-inline">
						<Icon
							:icon="getSortIcon(column.key)"
							class="ms-2 text-dark"
						/>
					</div>
				</div>
			</template>
			<div 
				v-if="props.actionConfig"
				class="d-flex justify-content-center fw-bold" 
				:style="`width: ${props.actionConfig.columnWidthPercent}%`"
			>
				{{ props.actionConfig.label }}
			</div>
		</div>
		<div class="border-start border-end" style="max-height: 85.6%; overflow-y: auto">
			<!-- render this if sort is not set -->
			<template v-if="!sortIsSet()" v-for="(group) in props.groupedRows.filter(group => group.rows.length)">
				<div class="header text-dark fw-bold w-100 py-1 ps-2">{{ group.groupLabel }}</div>
				<template v-for="(row, i) in group.rows">
					<div 
						class="d-flex w-100 ps-2 py-1" 
						:class="i % 2 === 0 ? 'bg-white' : 'bg-light'"
						style="gap: 1%;"
					>
						<template v-for="(column) in props.columns">
							<div :style="`width: ${column.widthPercent}%`">{{ row[column.key] }}</div>
						</template>
						<div 
							v-if="props.actionConfig"
							class="d-flex justify-content-center"
							:style="`width: ${props.actionConfig.columnWidthPercent}%`"
						>
							<template v-for="(action) in props.actionConfig?.actions">
								<div class="d-inline">
									<ChoiceButton
										:display-style="action.style"
										@click="action.action(row)"
										:size="ButtonDisplaySize.XS"
										:text="action.label"
									/>
								</div>
							</template>
						</div>
					</div>
				</template>
			</template>
			<!-- render this if sort is set -->
			<template v-else v-for="(row, i) in rows">
				<div 
					class="d-flex w-100 ps-2 py-1" 
					:class="i % 2 === 0 ? 'bg-white' : 'bg-light'"
					style="gap: 1%;"
				>
					<template v-for="(column) in props.columns">
						<div :style="`width: ${column.widthPercent}%`">{{ row[column.key] }}</div>
					</template>
					<div 
						v-if="props.actionConfig"
						class="d-flex justify-content-center"
						:style="`width: ${props.actionConfig.columnWidthPercent}%`"
					>
						<template v-for="(action) in props.actionConfig?.actions">
							<div class="d-inline">
								<ChoiceButton
									:display-style="action.style"
									@click="action.action(row)"
									:size="ButtonDisplaySize.XS"
									:text="action.label"
								/>
							</div>
						</template>
					</div>
				</div>
			</template>
		</div>
		<div 
			class="border rounded-bottom text-dark ps-2 py-1"
			style="font-size: x-small;"
		>
			<slot name="footer"></slot>
		</div>
	</div>
</template>